import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Package, Search, Truck, Clock, ExternalLink, Zap, Wind, Music } from 'lucide-react';

const categories = [
  "Zipper's Performance",'JNR Designed','Hertz Audio',
  'Harley-Davidson OEM','Indian Motorcycle','S&S Cycle','Andrews Products',
  "Screamin' Eagle",'Kuryakyn','Arlen Ness',
  'JIMS USA','Rivera Primo','Custom Fabricated','Vintage & NOS',
];

const steps = [
  { icon: <Phone size={28} />, num: '01', title: 'Tell Us What You Need', desc: "Call or reach out with the part number or description. The more detail, the better." },
  { icon: <Search size={28} />, num: '02', title: 'We Hunt It Down', desc: "We tap our network of suppliers, salvage contacts, and NOS sources to find what you need." },
  { icon: <Truck size={28} />, num: '03', title: 'We Source & Verify', desc: "Before anything ships, we confirm the part is correct and in the condition advertised." },
  { icon: <Clock size={28} />, num: '04', title: 'Ready When You Are', desc: "Part in hand and ready to install. We can put it on or hand it over if you prefer." },
];

const suppliers = [
  {
    name: "Zipper's Performance",
    url: 'https://www.zippersperformance.com',
    icon: <Zap size={32} />,
    tag: 'Performance Parts',
    desc: "One of the most respected names in V-Twin performance. Zipper's specializes in engine kits, cam and valve train components, top end and cylinder heads, intake systems, ignition, and bottom end builds for Milwaukee-Eight and Indian platforms.",
    specialties: [
      'Engine Kits & Stage Upgrades',
      'Cam / Valve Train',
      'Top End & Cylinder Heads',
      'Chain Conversion Kits',
      'Intake Systems',
      'Ignition & Electrical',
      'Bottom End & Oil Pump',
      'Drive Train Components',
    ],
  },
  {
    name: 'JNR Designed',
    url: 'https://www.jnrdesigned.com',
    icon: <Wind size={32} />,
    tag: 'Air Ride Suspension',
    desc: "The go-to source for Harley Touring air ride. JNR builds the most comfortable, affordable, and reliable air ride kits on the market, with a reputation for easy installation and quality components that last.",
    specialties: [
      'Complete Air Ride Kits',
      'Compressor Mounts',
      'Air Ride Components',
      'Optional Switch Controls',
      'Harley Touring Fitment',
      'Warranty-Backed Products',
    ],
  },
  {
    name: 'Hertz Audio',
    url: 'https://www.hertz-audio.com',
    icon: <Music size={32} />,
    tag: 'Motorcycle Audio',
    desc: "Premium audio for bikes and builds. Hertz covers the full spectrum from motorcycle and powersports audio to marine-grade components. Speakers, subwoofers, and amplifiers built for road performance.",
    specialties: [
      'Motorcycle & Powersports Audio',
      'Marine-Grade Components',
      'Speakers & Speaker Systems',
      'Subwoofers',
      'Amplifiers',
      'Car Audio Integration',
    ],
  },
];

const css = `
  .parts-page { background: #0d0d0d; }
  .parts-intro {
    max-width: 1180px; margin: 0 auto; padding: 80px 24px;
    display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: center;
  }
  .parts-text p { color: #888; line-height: 1.82; margin-bottom: 16px; font-size: 0.95rem; }
  .parts-text .btn-primary { margin-top: 20px; }
  .parts-box {
    background: #1a1a1a; border: 1px solid rgba(192,57,43,0.22);
    border-top: 3px solid #c0392b; padding: 36px;
  }
  .parts-box h3 { font-size: 1.15rem; text-transform: uppercase; color: #fff; letter-spacing: 0.04em; margin-bottom: 20px; }
  .parts-stat { display: flex; gap: 14px; align-items: flex-start; padding: 15px 0; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .parts-stat:last-child { border-bottom: none; }
  .parts-stat svg { color: #c0392b; flex-shrink: 0; margin-top: 2px; }
  .parts-stat strong { font-family: 'Oswald', sans-serif; font-size: 0.95rem; color: #fff; letter-spacing: 0.04em; display: block; margin-bottom: 3px; }
  .parts-stat span { font-size: 0.82rem; color: #777; }

  .suppliers-sec { background: #111; padding: 88px 0; }
  .suppliers-inner { max-width: 1180px; margin: 0 auto; padding: 0 24px; }
  .suppliers-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 28px; margin-top: 48px; }
  .supplier-card {
    background: #161616; border: 1px solid rgba(255,255,255,0.07);
    border-top: 3px solid #c0392b; padding: 36px 28px;
    display: flex; flex-direction: column; transition: border-color 0.2s, background 0.2s;
  }
  .supplier-card:hover { border-color: rgba(192,57,43,0.5); background: #1a1a1a; }
  .supplier-icon { color: #c0392b; margin-bottom: 16px; }
  .supplier-tag {
    font-family: 'Oswald', sans-serif; font-size: 0.68rem; font-weight: 600;
    letter-spacing: 0.22em; text-transform: uppercase; color: #d4a017; margin-bottom: 8px;
  }
  .supplier-name {
    font-family: 'Oswald', sans-serif; font-size: 1.4rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.04em; color: #fff; margin-bottom: 14px;
  }
  .supplier-desc { font-size: 0.86rem; color: #888; line-height: 1.78; margin-bottom: 22px; flex: 1; }
  .supplier-list { list-style: none; padding: 0; margin: 0 0 26px; border-top: 1px solid rgba(255,255,255,0.06); padding-top: 18px; }
  .supplier-list li {
    font-size: 0.8rem; color: #999; padding: 6px 0;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    display: flex; align-items: center; gap: 8px;
  }
  .supplier-list li:before { content: ''; width: 5px; height: 5px; background: #c0392b; flex-shrink: 0; }
  .supplier-link {
    display: inline-flex; align-items: center; gap: 8px;
    font-family: 'Oswald', sans-serif; font-size: 0.8rem; font-weight: 600;
    letter-spacing: 0.12em; text-transform: uppercase; color: #c0392b;
    text-decoration: none; transition: color 0.2s; margin-top: auto;
  }
  .supplier-link:hover { color: #e84234; }

  .cats-sec { background: #0d0d0d; padding: 80px 0; }
  .cats-inner { max-width: 1180px; margin: 0 auto; padding: 0 24px; }
  .cats-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 10px; margin-top: 44px; }
  .cat-chip {
    background: #1a1a1a; border: 1px solid rgba(255,255,255,0.07);
    padding: 14px 18px; font-family: 'Oswald', sans-serif; font-size: 0.82rem;
    font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase; color: #999;
    text-align: center; transition: all 0.2s;
  }
  .cat-chip:hover { border-color: rgba(192,57,43,0.4); color: #fff; background: rgba(192,57,43,0.07); }

  .process-sec { max-width: 1180px; margin: 0 auto; padding: 80px 24px; }
  .process-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 24px; margin-top: 48px; }
  .process-card { background: #1a1a1a; border: 1px solid rgba(255,255,255,0.06); padding: 30px 22px; position: relative; }
  .process-num {
    font-family: 'Bebas Neue', 'Oswald', sans-serif; font-size: 5rem;
    color: rgba(192,57,43,0.1); line-height: 1; position: absolute; top: 14px; right: 18px;
  }
  .process-card-icon { color: #c0392b; margin-bottom: 14px; position: relative; }
  .process-card h4 { font-size: 0.97rem; text-transform: uppercase; color: #fff; letter-spacing: 0.04em; margin-bottom: 9px; position: relative; }
  .process-card p { font-size: 0.84rem; color: #888; line-height: 1.7; position: relative; }

  .parts-cta { background: #c0392b; padding: 72px 0; }
  .parts-cta-inner { max-width: 1180px; margin: 0 auto; padding: 0 24px; text-align: center; }
  .parts-cta h2 { font-size: clamp(1.8rem,4vw,3rem); text-transform: uppercase; color: #fff; margin-bottom: 12px; }
  .parts-cta p { color: rgba(255,255,255,0.78); margin-bottom: 36px; }
  .parts-cta-btns { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; }
  .btn-white-2 {
    display: inline-flex; align-items: center; gap: 10px;
    background: #fff; color: #c0392b; padding: 14px 28px;
    font-family: 'Oswald', sans-serif; font-size: 0.9rem; font-weight: 700;
    letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none; transition: all 0.2s;
  }
  .btn-white-2:hover { background: #f5f0e8; transform: translateY(-2px); }
  .btn-ghost-2 {
    display: inline-flex; align-items: center; gap: 10px;
    background: transparent; color: #fff; border: 2px solid rgba(255,255,255,0.55);
    padding: 12px 26px; font-family: 'Oswald', sans-serif; font-size: 0.9rem; font-weight: 600;
    letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none; transition: all 0.2s;
  }
  .btn-ghost-2:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

  @media (max-width:1024px) {
    .suppliers-grid { grid-template-columns: 1fr; gap: 20px; }
    .cats-grid { grid-template-columns: repeat(3,1fr); }
    .process-grid { grid-template-columns: repeat(2,1fr); }
  }
  @media (max-width:768px) {
    .parts-intro { grid-template-columns: 1fr; gap: 40px; }
    .cats-grid { grid-template-columns: repeat(2,1fr); }
    .process-grid { grid-template-columns: 1fr; }
  }
`;

export default function Parts() {
  return (
    <>
      <style>{css}</style>
      <div className="page-header">
        <div className="container">
          <span className="section-label">Hard to Find</span>
          <h1>Parts Sourcing</h1>
          <p>We track down rare, discontinued, and hard-to-find parts so your build or repair never stalls.</p>
        </div>
      </div>
      <div className="parts-page">
        <div className="parts-intro">
          <div className="parts-text">
            <span className="section-label">The Problem We Solve</span>
            <h2 className="section-title">Can't Find the Part? We Can.</h2>
            <div className="divider" />
            <p>Finding parts for older American V-Twins or rare custom builds can be a nightmare. Discontinued OEM parts, hard-to-match custom components, and obscure sourcing requirements stop projects dead in their tracks.</p>
            <p>Wengers Cycle Works has the network and the experience to find what you need. We've hunted down everything from NOS pistons to discontinued electrical components. If it exists, we'll find it.</p>
            <a href="tel:7172785897" className="btn-primary"><Phone size={16} /> Tell Us What You Need</a>
          </div>
          <div className="parts-box">
            <h3>What We Source</h3>
            <div className="parts-stat"><Package size={20} /><div><strong>OEM & Aftermarket</strong><span>Factory and third-party components for any American V-Twin</span></div></div>
            <div className="parts-stat"><Search size={20} /><div><strong>NOS (New Old Stock)</strong><span>Genuine unused parts from old inventory, the good stuff</span></div></div>
            <div className="parts-stat"><Truck size={20} /><div><strong>Discontinued Parts</strong><span>No longer made, but not impossible to find if you know where to look</span></div></div>
            <div className="parts-stat"><Package size={20} /><div><strong>Custom Fabricated</strong><span>When a part truly doesn't exist, we can make it</span></div></div>
          </div>
        </div>

        <div className="suppliers-sec">
          <div className="suppliers-inner">
            <span className="section-label">Trusted Partners</span>
            <h2 className="section-title">Our Preferred Suppliers</h2>
            <div className="divider" />
            <div className="suppliers-grid">
              {suppliers.map((s, i) => (
                <div className="supplier-card" key={i}>
                  <div className="supplier-icon">{s.icon}</div>
                  <div className="supplier-tag">{s.tag}</div>
                  <div className="supplier-name">{s.name}</div>
                  <p className="supplier-desc">{s.desc}</p>
                  <ul className="supplier-list">
                    {s.specialties.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="supplier-link">
                    Visit {s.name} <ExternalLink size={14} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="cats-sec">
          <div className="cats-inner">
            <span className="section-label">Brands & Sources</span>
            <h2 className="section-title">We Source From</h2>
            <div className="divider" />
            <div className="cats-grid">
              {categories.map((c, i) => <div className="cat-chip" key={i}>{c}</div>)}
            </div>
          </div>
        </div>

        <div className="process-sec">
          <span className="section-label">How It Works</span>
          <h2 className="section-title">Our Parts Process</h2>
          <div className="divider" />
          <div className="process-grid">
            {steps.map((s, i) => (
              <div className="process-card" key={i}>
                <div className="process-num">{s.num}</div>
                <div className="process-card-icon">{s.icon}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="parts-cta">
          <div className="parts-cta-inner">
            <h2>Need a Part You Can't Find?</h2>
            <p>Reach out and tell us what you're looking for. We'll do the hunting.</p>
            <div className="parts-cta-btns">
              <a href="tel:7172785897" className="btn-white-2"><Phone size={16} /> (717) 278-5897</a>
              <Link to="/contact" className="btn-ghost-2">Send a Message</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
